<template>
  <b-container fluid class="mt-4 px-lg-5">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="text-primary font-weight-bold mb-0">Estadísticas de Ventas</h2>
    </div>

    <!-- Filtros -->
    <b-card class="mb-4 glass-card border-0">
      <b-row align-v="center">
        <b-col md="4" class="mb-3 mb-md-0">
          <label for="start_date" class="font-weight-bold text-muted small text-uppercase">Fecha de Inicio</label>
          <b-form-input
            id="start_date"
            type="date"
            v-model="filters.start_date"
            class="shadow-sm border-0 bg-light"
          ></b-form-input>
        </b-col>
        <b-col md="4" class="mb-3 mb-md-0">
          <label for="end_date" class="font-weight-bold text-muted small text-uppercase">Fecha de Fin</label>
          <b-form-input
            id="end_date"
            type="date"
            v-model="filters.end_date"
            class="shadow-sm border-0 bg-light"
          ></b-form-input>
        </b-col>
        <b-col md="4" class="d-flex align-items-end">
          <b-button variant="primary" block @click="fetchStatistics" :disabled="loading" class="shadow-sm py-2 font-weight-bold">
            <b-spinner small v-if="loading" class="mr-2"></b-spinner>
            {{ loading ? 'Calculando...' : 'Consultar Estadísticas' }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div v-if="statistics">
      <!-- Tarjetas de Resumen (KPIs) -->
      <b-row class="mb-4 text-center">
        <b-col md="4" class="mb-3 mb-md-0">
          <div class="kpi-card glass-card p-4 h-100 d-flex flex-column justify-content-center" style="border-top: 4px solid #3b82f6;">
            <h5 class="text-uppercase mb-2 text-muted font-weight-bold" style="letter-spacing: 1px; font-size: 0.85rem;">Cantidad de Ventas</h5>
            <h2 class="display-4 font-weight-bold mb-0 text-dark">{{ statistics.total_sales_count }}</h2>
          </div>
        </b-col>
        <b-col md="4" class="mb-3 mb-md-0">
          <div class="kpi-card glass-card p-4 h-100 d-flex flex-column justify-content-center" style="border-top: 4px solid #10b981;">
            <h5 class="text-uppercase mb-2 text-muted font-weight-bold" style="letter-spacing: 1px; font-size: 0.85rem;">Monto Total</h5>
            <h2 class="font-weight-bold mb-0 text-dark" style="font-size: 2.5rem;">${{ formatCurrency(statistics.total_sales_amount) }}</h2>
          </div>
        </b-col>
        <b-col md="4">
          <div class="kpi-card glass-card p-4 h-100 d-flex flex-column justify-content-center" style="border-top: 4px solid #8b5cf6;">
            <h5 class="text-uppercase mb-2 text-muted font-weight-bold" style="letter-spacing: 1px; font-size: 0.85rem;">Ganancia Neta</h5>
            <h2 class="font-weight-bold mb-0 text-dark" style="font-size: 2.5rem;">${{ formatCurrency(statistics.net_profit) }}</h2>
          </div>
        </b-col>
      </b-row>

      <!-- Gráfico de Tendencias -->
      <b-row class="mb-4">
        <b-col cols="12">
          <b-card class="glass-card border-0" body-class="p-4">
            <h5 class="mb-4 font-weight-bold text-secondary">Evolución en el Tiempo</h5>
            <apexchart 
              type="area" 
              height="350" 
              :options="chartOptions" 
              :series="chartSeries"
            ></apexchart>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <!-- Ventas por Categoría -->
        <b-col lg="6" class="mb-4">
          <b-card class="glass-card border-0 h-100" body-class="p-0">
            <div class="p-4 border-bottom">
              <h5 class="mb-0 font-weight-bold text-secondary">Ventas por Categoría</h5>
            </div>
            <div class="p-3">
              <b-table
                striped
                hover
                responsive
                borderless
                :items="statistics.sales_by_category"
                :fields="categoryFields"
                show-empty
                empty-text="No hay datos para mostrar"
                thead-class="text-muted text-uppercase small"
              >
                <template #cell(name)="data">
                  <span class="font-weight-bold">{{ data.value }}</span>
                </template>
                <template #cell(total_amount)="data">
                  ${{ formatCurrency(data.value) }}
                </template>
                <template #cell(profit)="data">
                  <span class="text-success font-weight-bold">${{ formatCurrency(data.value) }}</span>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>

        <!-- Ventas por Producto -->
        <b-col lg="6" class="mb-4">
          <b-card class="glass-card border-0 h-100" body-class="p-0">
            <div class="p-4 border-bottom d-flex justify-content-between align-items-center">
              <h5 class="mb-0 font-weight-bold text-secondary">Ventas por Producto</h5>
              <b-button variant="success" size="sm" @click="exportToExcel">
                <b-icon icon="file-earmark-excel" class="mr-1"></b-icon> Exportar Excel
              </b-button>
            </div>
            <div class="p-3">
              <b-table
                striped
                hover
                responsive
                borderless
                :items="statistics.sales_by_product"
                :fields="productFields"
                show-empty
                empty-text="No hay datos para mostrar"
                thead-class="text-muted text-uppercase small"
              >
                <template #cell(name)="data">
                  <span class="font-weight-bold">{{ data.value }}</span>
                </template>
                <template #cell(total_amount)="data">
                  ${{ formatCurrency(data.value) }}
                </template>
                <template #cell(profit)="data">
                  <span class="text-success font-weight-bold">${{ formatCurrency(data.value) }}</span>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>
      </b-row>
      
      <!-- Búsqueda Específica de Artículo -->
      <b-row class="mb-4">
        <b-col cols="12">
          <b-card class="glass-card border-0" body-class="p-4">
            <h5 class="mb-4 font-weight-bold text-secondary">Análisis por Artículo Específico</h5>
            <b-row align-v="center" class="mb-4">
              <b-col md="8">
                <b-input-group>
                  <b-form-input
                    v-model="articleSearchQuery"
                    placeholder="Buscar artículo por nombre, código..."
                    @keyup.enter="searchArticleForStats"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button variant="primary" @click="searchArticleForStats" :disabled="loadingArticleSearch">
                      <b-spinner small v-if="loadingArticleSearch" class="mr-1"></b-spinner>
                      Buscar
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
            </b-row>

            <!-- Resultados de la búsqueda del artículo para seleccionar -->
            <div v-if="articleSearchResults.length > 0" class="mb-4 border rounded p-2" style="max-height: 200px; overflow-y: auto">
              <b-table
                :items="articleSearchResults"
                :fields="articleSearchFields"
                small hover striped responsive
                @row-clicked="selectArticleForStats"
                class="mb-0 cursor-pointer"
              ></b-table>
            </div>

            <!-- Estadísticas del artículo seleccionado -->
            <div v-if="selectedArticleStats" class="mt-4 p-4 border rounded bg-light">
              <h6 class="font-weight-bold mb-3 text-primary">Resultados para: {{ selectedArticle ? selectedArticle.description : '' }}</h6>
              <b-row class="text-center mb-4">
                <b-col md="4">
                  <h6 class="text-muted text-uppercase small font-weight-bold">Cantidad Total Vendida</h6>
                  <h3 class="text-dark">{{ selectedArticleStats.total_quantity }}</h3>
                </b-col>
                <b-col md="4">
                  <h6 class="text-muted text-uppercase small font-weight-bold">Monto Total</h6>
                  <h3 class="text-success">${{ formatCurrency(selectedArticleStats.total_amount) }}</h3>
                </b-col>
              </b-row>
              <h6 class="mb-3 font-weight-bold text-secondary">Ventas por Día</h6>
              <apexchart 
                type="bar" 
                height="250" 
                :options="articleChartOptions" 
                :series="articleChartSeries"
              ></apexchart>
            </div>
            <b-alert v-if="articleErrorMessage" show variant="warning" class="mt-3">
              {{ articleErrorMessage }}
            </b-alert>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Mensaje cuando no hay búsqueda o hubo un error -->
    <b-alert v-if="!statistics && !loading && errorMessage" show variant="danger" class="mt-3 shadow-sm border-0 rounded-lg">
      {{ errorMessage }}
    </b-alert>
  </b-container>
</template>

<script>
import api from '@/axios'
import * as XLSX from 'xlsx'
import ArticleService from '@/services/article.service'

export default {
  name: 'StatisticsView',
  data() {
    return {
      filters: {
        start_date: '',
        end_date: ''
      },
      loading: false,
      statistics: null,
      errorMessage: '',
      
      // Chart Data
      chartSeries: [],
      chartOptions: {
        chart: {
          type: 'area',
          toolbar: { show: false },
          fontFamily: 'Outfit, sans-serif'
        },
        colors: ['#10b981', '#8b5cf6'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        xaxis: {
          type: 'datetime',
          categories: [],
          labels: { style: { colors: '#64748b' } }
        },
        yaxis: {
          labels: { 
            formatter: (value) => { return "$" + this.formatCurrency(value) },
            style: { colors: '#64748b' } 
          }
        },
        tooltip: {
          x: { format: 'dd MMM yyyy' },
          theme: 'light'
        }
      },

      // Table definitions
      categoryFields: [
        { key: 'name', label: 'Categoría', sortable: true },
        { key: 'quantity', label: 'Cant.', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'total_amount', label: 'Monto Total', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'profit', label: 'Ganancia', sortable: true, thClass: 'text-right', tdClass: 'text-right' }
      ],
      productFields: [
        { key: 'code', label: 'Código', sortable: true },
        { key: 'name', label: 'Producto', sortable: true },
        { key: 'quantity', label: 'Cant.', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'total_amount', label: 'Monto Total', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'profit', label: 'Ganancia', sortable: true, thClass: 'text-right', tdClass: 'text-right' }
      ],

      // Article specific analysis
      articleSearchQuery: '',
      loadingArticleSearch: false,
      articleSearchResults: [],
      selectedArticle: null,
      selectedArticleStats: null,
      articleErrorMessage: '',
      articleSearchFields: [
        { key: "code", label: "Código" },
        { key: "description", label: "Descripción" }
      ],
      articleChartSeries: [],
      articleChartOptions: {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Outfit, sans-serif' },
        colors: ['#3b82f6', '#10b981'],
        plotOptions: { bar: { columnWidth: '50%' } },
        dataLabels: { enabled: false },
        xaxis: { type: 'datetime', categories: [] },
        yaxis: [
          { title: { text: 'Cantidad Vendida' } }
        ]
      }
    }
  },
  mounted() {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    
    this.filters.start_date = firstDay.toISOString().split('T')[0]
    this.filters.end_date = today.toISOString().split('T')[0]
  },
  methods: {
    async fetchStatistics() {
      if (!this.filters.start_date || !this.filters.end_date) {
        this.errorMessage = 'Por favor, selecciona un rango de fechas válido.'
        return
      }

      this.loading = true
      this.errorMessage = ''
      
      try {
        const response = await api.get('/statistics', {
          params: {
            start_date: this.filters.start_date,
            end_date: this.filters.end_date
          }
        })
        this.statistics = response.data
        this.updateChart()
      } catch (error) {
        console.error('Error fetching statistics:', error)
        this.errorMessage = 'Hubo un error al obtener las estadísticas. Por favor intenta de nuevo.'
        this.statistics = null
      } finally {
        this.loading = false
      }
    },
    updateChart() {
      if (!this.statistics || !this.statistics.timeline) return;

      const dates = this.statistics.timeline.map(t => t.date);
      const amounts = this.statistics.timeline.map(t => t.total_amount);
      const profits = this.statistics.timeline.map(t => t.profit);

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: dates
        }
      };

      this.chartSeries = [
        { name: 'Monto Total', data: amounts },
        { name: 'Ganancia Neta', data: profits }
      ];
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    exportToExcel() {
      if (!this.statistics || !this.statistics.sales_by_product) return;
      
      const dataToExport = this.statistics.sales_by_product.map(p => ({
        'Código': p.code,
        'Producto': p.name,
        'Cantidad Vendida': p.quantity,
        'Monto Total ($)': Number(p.total_amount).toFixed(2),
        'Ganancia ($)': Number(p.profit).toFixed(2)
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas por Articulo');
      
      XLSX.writeFile(workbook, `Ventas_Articulos_${this.filters.start_date}_al_${this.filters.end_date}.xlsx`);
    },
    async searchArticleForStats() {
      if (!this.articleSearchQuery.trim()) return;
      this.loadingArticleSearch = true;
      this.articleErrorMessage = '';
      this.articleSearchResults = [];
      try {
        const res = await ArticleService.search(this.articleSearchQuery.trim());
        this.articleSearchResults = res.data;
        if (this.articleSearchResults.length === 0) {
          this.articleErrorMessage = 'No se encontraron artículos con ese criterio.';
        }
      } catch (err) {
        this.articleErrorMessage = 'Error al buscar el artículo.';
      } finally {
        this.loadingArticleSearch = false;
      }
    },
    async selectArticleForStats(article) {
      this.selectedArticle = article;
      this.articleSearchResults = [];
      this.articleSearchQuery = '';
      this.articleErrorMessage = '';
      await this.fetchArticleStats();
    },
    async fetchArticleStats() {
      if (!this.selectedArticle) return;
      try {
        const response = await api.get(`/statistics/article/${this.selectedArticle.id}`, {
          params: {
            start_date: this.filters.start_date,
            end_date: this.filters.end_date
          }
        });
        this.selectedArticleStats = response.data;
        
        // Update article chart
        const timeline = this.selectedArticleStats.timeline;
        this.articleChartOptions = {
          ...this.articleChartOptions,
          xaxis: { ...this.articleChartOptions.xaxis, categories: timeline.map(t => t.date) }
        };
        this.articleChartSeries = [
          { name: 'Cantidad Vendida', type: 'column', data: timeline.map(t => t.quantity_sold) },
          { name: 'Monto ($)', type: 'line', data: timeline.map(t => t.total_amount) }
        ];
      } catch (error) {
        this.articleErrorMessage = 'Error al obtener las estadísticas del artículo.';
        console.error(error);
      }
    }
  }
}
</script>

<style scoped>
.kpi-card {
  transition: transform 0.3s ease;
}
.kpi-card:hover {
  transform: translateY(-5px);
}
/* Estilo para las tablas transparentes dentro del glass card */
::v-deep .table {
  color: #1e293b;
}
::v-deep .table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}
::v-deep .table-hover tbody tr:hover {
  background-color: rgba(59, 130, 246, 0.05);
}
.cursor-pointer tbody tr {
  cursor: pointer;
}
</style>
